import {useEffect, useRef, useState} from 'react'
import {metresBetween} from '@/utils/polyline'

/**
 * "AM I THERE YET" — answered from position, not from a button.
 *
 * ⚠️ THERE IS NO ARRIVAL ENDPOINT ANYWHERE IN THE SYSTEM. Nothing is posted when this
 * turns true. Both sides infer arrival independently by watching the other's shared
 * position against their own copy of the booth coordinate, which is why the coordinate
 * itself is the thing that must not drift — see `yyzArrivalEntries`.
 */

/* ⚠️ THE RADIUS IS THE BACKEND'S, passed in — `pickup_arrival.passenger.from_airport.radius_m`
   (Admin → Settings → Pickup Arrival), read by `usePickupArrivalConfig`. It used to be a fifty
   metre constant here, "identical to the driver's"; the driver's now comes from the same
   setting, which is what keeps the two sides agreeing about when a passenger has arrived.
   No radius (config not loaded, or the switch is off) means this never reports arrival. */

/**
 * HOW BAD A FIX MAY BE AND STILL COUNT.
 *
 * ⚠️ THE MOBILE APPS CHECK NOTHING HERE, AND A BROWSER CANNOT AFFORD THAT. On a phone the
 * fix is OS-fused from GPS, wifi and cell; indoors at a terminal it is metres out. A laptop
 * or desktop browser falls back to wifi or IP geolocation, which routinely reports a
 * position kilometres away with an `accuracy` that honestly says so — and a 50 m test that
 * ignores accuracy will happily "arrive" on one of those the moment it lands near the booth
 * by chance.
 *
 * Matching the threshold is the natural bound: a fix whose own error bar is wider than the
 * radius cannot answer the question the radius asks.
 *
 * ⚠️ AND IT IS THE ONLY THING FILTERED. The threshold and the booth coordinates stay
 * exactly as the mobile apps have them, so this makes the web stricter about what it
 * BELIEVES, never about where the finish line is.
 */
export const MAX_ARRIVAL_ACCURACY_METERS = 50

/**
 * HOW MANY GOOD FIXES IN A ROW.
 *
 * ⚠️ TWO, BECAUSE ONE IS A COIN TOSS. Browser positioning jumps: a single fix can land on
 * the booth and the next be three hundred metres away. Arrival LATCHES and ends the walk,
 * so it is the one decision here that cannot be taken back — worth the extra second it
 * costs to see the same answer twice.
 */
const CONSECUTIVE_FIXES_REQUIRED = 2

/**
 * @param {{lat: number, lng: number, accuracy_m?: number|null}|null} position  the
 *   passenger, from `useMyLocation`
 * @param {[number, number]|null} pickup  the booth, `[lng, lat]`
 * @param {number|null} radiusM  "at the pickup", metres — from `usePickupArrivalConfig`
 */
export function useArrivedAtPickupPoint(position, pickup, radiusM) {
    const [arrived, setArrived] = useState(false)
    /* A ref, not state: the run of good fixes is bookkeeping on the way to one boolean, and
       re-rendering the walk screen for each step of it would be a re-render per second. */
    const streak = useRef(0)

    const lat = position?.lat
    const lng = position?.lng
    const accuracy = position?.accuracy_m

    useEffect(() => {
        /* ⚠️ IT LATCHES. Once true it never goes false again, and that is the whole point:
           a fix that drifts sixty metres a moment after arrival would otherwise throw the
           passenger back into a walk they have finished, standing beside the car. */
        if (arrived || !pickup || !radiusM || !Number.isFinite(lat) || !Number.isFinite(lng)) return

        /* ⚠️ A MISSING `accuracy` IS ACCEPTED, NOT REJECTED. Some browsers omit it, and
           refusing every fix that does not self-report would mean those users never arrive
           at all. Only an accuracy that is present AND too wide disqualifies one. */
        const tooVague = Number.isFinite(accuracy) && accuracy > MAX_ARRIVAL_ACCURACY_METERS
        const withinRadius = metresBetween(
            {lat, lng},
            {lat: pickup[1], lng: pickup[0]},
        ) <= radiusM

        if (tooVague || !withinRadius) {
            streak.current = 0
            return
        }

        streak.current += 1
        if (streak.current >= CONSECUTIVE_FIXES_REQUIRED) setArrived(true)
    }, [arrived, lat, lng, accuracy, pickup, radiusM])

    return arrived
}

export default useArrivedAtPickupPoint
